import { useDispatch, useSelector } from 'react-redux';
import { configureStore } from '@reduxjs/toolkit';
import company from './company';
import creative from './creative';
import dashboardAgent from './dashboardAgent';
import modals from './modals';
import navbar from './navbar';
import networks from './networks';
import user from './user';

// https://redux-toolkit.js.org/usage/usage-with-typescript
export const reduxStore = configureStore({
  reducer: {
    user,
    modals,
    navbar,
    company,
    networks,
    creative,
    dashboardAgent
  },
  // Redux Toolkit ships a serializableCheck middleware that warns whenever a non-plain-JS value
  // (a class instance, a function, a Promise, etc.) is placed in an action or in the store state.
  // The warning exists because non-serializable values break time-travel debugging and
  // state persistence. We intentionally violate this rule in two places inside the modals slice,
  // so we suppress the warnings for those specific paths rather than disabling the check globally.
  //
  // 1. ignoredActionPaths — paths inside a dispatched *action object* to skip.
  //    - error.reportError: the raw Error/FetchError instance passed so the modals reducer can
  //      report it before stripping it from state. It never reaches the store.
  //    - error.onOkay / areYouSure.onOkay / areYouSure.onCancel: callback
  //      functions that travel inside the action so the reducer can persist them (see below).
  //
  // 2. ignoredPaths — dot-separated paths inside the *store state* to skip.
  //    - modals.error.onOkay and modals.areYouSure.onOkay / onCancel: these are the
  //      Promise resolve/reject callbacks placed in state by dispatchError / useSetAreYouSure.
  //      Storing them lets those helpers return a Promise that resolves only when the user
  //      dismisses the modal — callers can `await dispatchError(...)` to gate follow-up logic
  //      on the user's response. There is no serializable equivalent for this pattern.
  middleware: getDefaultMiddleware =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActionPaths: [
          'error.reportError',
          'error.onOkay',
          'areYouSure.onOkay',
          'areYouSure.onCancel'
        ],
        ignoredPaths: [
          'modals.error.onOkay',
          'modals.areYouSure.onOkay',
          'modals.areYouSure.onCancel'
        ]
      }
    })
});

export type RootState = ReturnType<typeof reduxStore.getState>;
export type AppDispatch = typeof reduxStore.dispatch;

export const useAppSelector = useSelector.withTypes<RootState>();
export const useAppDispatch = useDispatch.withTypes<AppDispatch>();

type DeepPartial<T> = T extends object ? { [K in keyof T]?: DeepPartial<T[K]> } : T;

export const createTestReduxStore = (preloadedState?: DeepPartial<RootState>) =>
  configureStore({
    reducer: { user, modals, navbar, company, networks, creative, dashboardAgent },
    preloadedState: preloadedState as Partial<RootState>
  });
